import React, { useEffect, useMemo, useState } from 'react';
import { Carousel } from '@mantine/carousel';
import AutoScroll from 'embla-carousel-auto-scroll';
import Autoplay from 'embla-carousel-autoplay';
import { Box, Overlay } from '@mantine/core';
import { useReducedMotion } from '@mantine/hooks';
import PuckInlineRichText from '../../proposal/ProposalRichText/PuckInlineRichText';
import { textRoleCss } from '../../../puck/fields/TextRolesField';
import CtaButton from '../shared/CtaButton';
import { puckArrayFieldPath } from '../../../utils/puckContentPath';
import { MediaStack, isMediaPresent, useMediaAspectRatio } from '../../../media';
import { withVariablePlaceholder } from '../../../media/variableMedia';
import { VariableMediaRefresh } from '../../../media/VariableImage';
import { resolveTextBackgroundColor } from '../../../utils/textBackgroundStyle';
import { cssLengthToPx } from '../../../utils/proposalUnits';
import { slideButtonProps } from './carouselDefaults';
import { subscribeSlidePreview } from '../../../utils/carouselSlidesRegistry';
import {
  carouselAutoScrollOptions,
  carouselAutoplayAllowed,
  carouselAutoplayOptions,
  carouselContentMediaStyle,
  carouselLoopRepeat,
  carouselEdgeFadeWidthPx,
  carouselEmblaOptions,
  carouselOrientation,
  carouselViewportHeightPx,
  carouselSlideGap,
  carouselSlideSize,
  slideTextVisible,
} from './carouselLayout';
import CarouselThumbnails from './CarouselThumbnails';
import { carouselThumbnails, thumbnailsUnavailableReason } from './carouselThumbnailSources';
import classes from './CarouselBlock.module.css';

const CarouselSlide = ({
  slide,
  slideIndex,
  blockId,
  puck,
  mediaFit = 'cover',
  autoWidth = false,
  compact = false,
  showSlideText = true,
  textStyles = null,
}) => {
  const isEditing = !!puck?.isEditing;
  const slideBackground = resolveTextBackgroundColor(slide.slideBackground);
  const backgroundMedia = withVariablePlaceholder(slide.backgroundMedia, { isEditing });
  const contentMedia = withVariablePlaceholder(slide.contentMedia, { isEditing });
  const hasBackgroundMedia = isMediaPresent(backgroundMedia);
  const hasContentMedia = isMediaPresent(contentMedia);
  // Ratio réel du fichier : il donne sa largeur au média, donc à une slide
  // auto. Hook appelé sans condition (règle des hooks) ; renvoie le repli
  // quand il n'y a pas de média.
  const contentMediaRatio = useMediaAspectRatio(contentMedia);
  // Un texte vide garde une zone cliquable en édition, mais compacte quand la
  // slide porte un média : deux blocs de 40 et 48 px au milieu de l'image la
  // masquaient. Sortir ces zones du flux a été essayé et retiré — la slide n'y
  // gagnait plus aucune largeur et s'effondrait.
  const textMinHeight = (base) => {
    if (!isEditing) return undefined;
    return hasBackgroundMedia || hasContentMedia ? 0 : base;
  };

  // Un seul arbre de contenu : la branche « sans média de fond » ne rendait que
  // le titre, la description et le bouton saisis restaient invisibles.
  const content = (
    <Box
      className={[
        classes.slideContent,
        autoWidth ? classes.slideContentAuto : '',
        compact ? classes.slideContentCompact : '',
      ].filter(Boolean).join(' ')}
    >
      {slideTextVisible({ showSlideText, isEditing, text: slide.title }) && (
        <PuckInlineRichText
          id={blockId}
          puck={puck}
          html={slide.title}
          contentPath={puckArrayFieldPath('slides', slideIndex, 'title')}
          className={classes.slideTitle}
          style={textRoleCss(textStyles, 'title')}
          minHeight={textMinHeight(40)}
          placeholder="Titre"
        />
      )}
      {hasContentMedia && (
        <Box
          className={`${classes.slideContentMedia} ${autoWidth ? classes.slideContentMediaAuto : ''}`.trim()}
          style={carouselContentMediaStyle({
            contentMediaWidth: slide.contentMediaWidth,
            maxSlidesVisible: autoWidth,
            ratio: contentMediaRatio,
          })}
        >
          {/* `contain` + hauteur bornée : toutes les slides donnent la même
              hauteur au média, sans recadrage (bandeau de logos). */}
          <MediaStack
            media={contentMedia}
            mediaFit="contain"
            isEditing={isEditing}
            asBackground
            fillContainer
            backgroundColor=""
          />
        </Box>
      )}
      {slideTextVisible({ showSlideText, isEditing, text: slide.description }) && (
        <PuckInlineRichText
          id={blockId}
          puck={puck}
          html={slide.description}
          contentPath={puckArrayFieldPath('slides', slideIndex, 'description')}
          className={classes.slideDescription}
          style={textRoleCss(textStyles, 'description')}
          minHeight={textMinHeight(48)}
          placeholder="Description"
        />
      )}
      {/* Le bouton est piloté par son interrupteur, plus par « libellé ET lien
          remplis » : il disparaissait en silence dès qu'un des deux manquait.
          En édition il n'est pas un lien, sinon un clic de saisie navigue. */}
      {slide.ctaEnabled && (
        <CtaButton
          id={blockId}
          puck={puck}
          label={slide.ctaLabel}
          contentPath={puckArrayFieldPath('slides', slideIndex, 'ctaLabel')}
          url={slide.ctaUrl}
          link={slide.ctaLink}
          {...slideButtonProps(slide)}
          className={classes.slideCta}
          labelClassName={classes.slideCtaLabel}
        />
      )}
    </Box>
  );

  return (
    <Carousel.Slide>
      <Box
        className={classes.slide}
        style={{ backgroundColor: slideBackground }}
        data-has-bg-media={hasBackgroundMedia ? '' : undefined}
      >
        <VariableMediaRefresh
          blockId={blockId}
          path={puckArrayFieldPath('slides', slideIndex, 'backgroundMedia')}
          media={slide.backgroundMedia}
          isEditing={isEditing}
        />
        {hasBackgroundMedia ? (
          <MediaStack
            media={backgroundMedia}
            mediaFit={mediaFit}
            isEditing={isEditing}
            asBackground
            fillContainer
            backgroundColor={slide.slideBackground ?? ''}
            overlay={<Overlay color="#000" opacity={0.35} zIndex={0} />}
            className={classes.mediaStack}
            contentClassName={classes.slideContentWrap}
          >
            {content}
          </MediaStack>
        ) : (
          <Box className={classes.slideFallback}>{content}</Box>
        )}
      </Box>
    </Carousel.Slide>
  );
};

const CarouselBlock = ({
  id,
  slides = [],
  /** Hauteur des slides — longueur CSS (champ px↔rem) ; Mantine attend un px. */
  height = '25rem',
  /** Axe vertical : hauteur de la fenêtre, les slides gardant la leur. */
  stripHeight = '31.25rem',
  loop = true,
  indicators = true,
  controls = true,
  autoplay = false,
  /** Temps d'arrêt sur chaque slide (hors ruban continu), en ms. */
  autoplayDelay,
  autoplayPauseOnHover = true,
  scrollSpeed = 2,
  /** Ruban sans arrêt qui remplit la largeur, au lieu de N slides par vue. */
  maxSlidesVisible = false,
  /** Axe du ruban continu : 'horizontal' | 'vertical'. */
  scrollAxis = 'horizontal',
  /** Sens dans cet axe : 'forward' (droite→gauche, bas→haut) | 'backward'. */
  scrollDirection = 'forward',
  slidesVisible = 1,
  edgeFade = true,
  edgeFadeWidth = '3rem',
  slideGap = '1rem',
  /** S'applique au média de fond des slides (le média de contenu est `contain`). */
  mediaFit = 'cover',
  /** Décoché (bandeau de logos) : la slide n'est que son média. */
  showSlideText = true,
  /** Style par défaut des textes des slides, par rôle (`title`, `description`). */
  textStyles = null,
  thumbnails = false,
  puck,
}) => {
  const isEditing = !!puck?.isEditing;
  const reducedMotion = useReducedMotion();
  const autoplayEnabled = carouselAutoplayAllowed({ autoplay, isEditing, reducedMotion });
  const [embla, setEmbla] = useState(null);
  const showThumbnails = thumbnails && !thumbnailsUnavailableReason({ maxSlidesVisible, scrollAxis, slidesVisible, slides });
  const thumbnailImages = showThumbnails ? carouselThumbnails(slides) : [];

  // L'œil d'une slide dans la sidebar amène le canvas sur cette slide.
  useEffect(() => {
    if (!isEditing || !embla) return undefined;
    return subscribeSlidePreview((blockId, slideIndex) => {
      if (blockId === id) embla.scrollTo(slideIndex);
    });
  }, [isEditing, embla, id]);

  // Changer de slide relance le temps d'arrêt. Le plugin Autoplay ne surveille
  // que le glissement (pointerDown/Up) et le survol : un scrollNext()
  // programmatique — les flèches et les pastilles de Mantine — laissait courir
  // le compte à rebours de la slide précédente, qui pouvait donc expirer
  // aussitôt après un clic. `reset()` ne fait rien si l'autoplay est déjà à
  // l'arrêt (pause au survol), il ne le ressuscite pas.
  useEffect(() => {
    if (!embla || !autoplayEnabled) return undefined;
    const resetDwell = () => embla.plugins()?.autoplay?.reset();
    embla.on('select', resetDwell);
    return () => {
      embla.off('select', resetDwell);
    };
  }, [embla, autoplayEnabled]);

  const fadeWidthPx = carouselEdgeFadeWidthPx({ maxSlidesVisible, edgeFade, edgeFadeWidth });
  const gapPx = carouselSlideGap(slideGap);
  const orientation = carouselOrientation({ scrollAxis });
  const isVertical = orientation === 'vertical';
  const slideHeightPx = cssLengthToPx(height);
  const viewportHeightPx = carouselViewportHeightPx({
    vertical: isVertical,
    maxSlidesVisible,
    heightPx: slideHeightPx,
    stripHeightPx: cssLengthToPx(stripHeight),
    slidesVisible,
    slideGapPx: gapPx,
  });

  // Embla coupe la boucle quand les slides ne débordent pas du cadre : le ruban
  // s'arrête alors sur la dernière. On répète le jeu de slides jusqu'à déborder,
  // comme un bandeau défilant. Jamais en édition : les copies n'ont pas de
  // contenu propre, et une slide dupliquée ne serait pas éditable.
  const wantsSeamlessLoop = !isEditing && maxSlidesVisible && loop !== false;
  const [loopRepeat, setLoopRepeat] = useState(1);

  useEffect(() => {
    if (!embla || !wantsSeamlessLoop) {
      setLoopRepeat(1);
      return undefined;
    }
    const measure = () => {
      const viewport = embla.rootNode();
      // Toujours le PREMIER jeu : sa taille ne dépend pas du nombre de
      // copies, donc la mesure ne se relance pas indéfiniment.
      const oneSet = embla.slideNodes().slice(0, slides.length);
      const setWidth = oneSet.reduce(
        (total, node) => total + (isVertical ? node.offsetHeight : node.offsetWidth) + gapPx,
        0,
      );
      setLoopRepeat(carouselLoopRepeat({
        viewportWidth: (isVertical ? viewport?.clientHeight : viewport?.clientWidth) ?? 0,
        setWidth,
      }));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(embla.rootNode());
    embla.on('reInit', measure);
    return () => {
      observer.disconnect();
      embla.off('reInit', measure);
    };
  }, [embla, wantsSeamlessLoop, slides.length, gapPx, isVertical]);

  // Deux mécaniques d'autoplay : AutoScroll défile en continu (aucun arrêt
  // possible), Autoplay avance d'une slide puis attend `delay`.
  const autoplayPlugin = useMemo(() => {
    if (!autoplayEnabled) return null;
    if (maxSlidesVisible) {
      return AutoScroll(carouselAutoScrollOptions({
        scrollSpeed, autoplayPauseOnHover, scrollDirection,
      }));
    }
    return Autoplay(carouselAutoplayOptions({ autoplayDelay, autoplayPauseOnHover }));
  }, [
    autoplayEnabled, maxSlidesVisible, autoplayDelay, autoplayPauseOnHover,
    scrollSpeed, scrollDirection,
  ]);

  // Filet quand l'utilisateur vide la liste (les defaultProps en fournissent
  // deux au drop).
  if (!slides.length) {
    return (
      <Box className={classes.empty}>
        Carousel — ajoutez des slides dans les propriétés
      </Box>
    );
  }

  return (
    <Box
      w="100%"
      style={fadeWidthPx ? { '--carousel-edge-fade': `${fadeWidthPx}px` } : undefined}
    >
      <Carousel
        getEmblaApi={setEmbla}
        orientation={orientation}
        withIndicators={showThumbnails ? false : indicators}
        withControls={controls}
        height={viewportHeightPx}
        slideSize={carouselSlideSize({
          maxSlidesVisible, slidesVisible, vertical: isVertical, slideHeightPx, slideGapPx: gapPx,
        })}
        slideGap={gapPx}
        emblaOptions={carouselEmblaOptions({
          maxSlidesVisible,
          slidesVisible,
          slidesCount: slides.length,
          scrollSpeed,
          loop,
          autoplayEnabled,
          isEditing,
        })}
        plugins={autoplayPlugin ? [autoplayPlugin] : undefined}
        classNames={{
          indicator: classes.indicator,
          control: classes.control,
          ...(fadeWidthPx
            ? { viewport: isVertical ? classes.viewportFadedVertical : classes.viewportFaded }
            : {}),
        }}
      >
        {Array.from({ length: loopRepeat }, (_, copy) => slides.map((slide, i) => (
          <CarouselSlide
            // `slideIndex` reste celui de la slide d'origine : les copies
            // pointent sur le même contenu.
            key={`${copy}-${i}`}
            slide={slide}
            slideIndex={i}
            blockId={id}
            puck={puck}
            mediaFit={mediaFit}
            // Verticalement, une slide occupe toute la largeur : c'est sa
            // hauteur qui suit le contenu, pas sa largeur.
            autoWidth={maxSlidesVisible && !isVertical}
            // À la verticale, la hauteur d'une slide est explicite et souvent
            // basse : le padding large ne laisserait plus de place au média.
            compact={isVertical}
            showSlideText={showSlideText}
            textStyles={textStyles}
          />
        )))}
      </Carousel>
      {showThumbnails && <CarouselThumbnails embla={embla} images={thumbnailImages} />}
    </Box>
  );
};

export default CarouselBlock;
